import type {
  ActionApprovalRule,
  ApprovalMode,
  Autonomy,
  CompanyAnswers,
  Department,
  Goal,
  Industry,
  TeamBot,
} from "@rakazo/contracts";
import { DEPARTMENTS } from "@rakazo/contracts";
import { botWorkRulesSection } from "./bot-work-rules.js";
import { recipeLeadSection, roleRecipe } from "./role-recipes.js";

/**
 * Chief, the generic department defaults offered as built-in templates, and the approval
 * settings an autonomy level means. A company's own team is researched and proposed by Chief
 * (see auto-team.ts); nothing here is specific to an industry.
 */

interface DepartmentDefault {
  name: string;
  role: string;
  /** What the bot does, written into its instructions. */
  duties: readonly string[];
  zone: string;
}

export const CHIEF_KEY = "chief";
/** Chief's spawn key: the first-run bot of a wizard team. */
export const CHIEF_SPAWN_KEY = "onboarding:first";
export const CHIEF_NAME = "Chief";
/** The routine Chief gets when it is created, so it wakes on its own every morning. */
export const CHIEF_DAILY_CHECK_IN = {
  name: "Daily check-in",
  cron: "0 10 * * *",
  prompt:
    "Review open work, deadlines and pending decisions. Follow up with teammates on anything stalled or due soon. Post a brief update only if something needs the owner; otherwise stay silent.",
} as const;
/** Bots of the setup team carry this spawn key prefix; Chief's is the first-run key. */
export const TEAM_SPAWN_PREFIX = "onboarding:";

/**
 * Chief and the leads setup created: the bots that default to the strongest connected model.
 * Bots made later (helpers, one-off bots) follow the member's main model.
 */
export function isLeadBot(bot: { superBot: boolean; spawnKey: string | null }): boolean {
  return bot.superBot || Boolean(bot.spawnKey?.startsWith(TEAM_SPAWN_PREFIX));
}
export const CHIEF_ROLE =
  "Runs the team: checks everyone's work, nudges stalled jobs, keeps each bot in role and sends you one daily summary with the decisions you need to make.";

/**
 * Roles whose answers rest on facts that change (law, regulation, tax, tenders, employment). They
 * keep web search on and are told to check current official sources and cite them.
 */
export const RESEARCH_DEPARTMENTS: readonly Department[] = ["compliance", "finance", "bids", "hr"];
const RESEARCH_NAME =
  /\b(legal|law|compliance|regulat|financ|account|tax|tender|bid|hr|human resources|people|payroll)/i;

/** Whether a team bot is a research role: a research department, or a bot named like one. */
export function isResearchRole(bot: Pick<TeamBot, "key" | "name" | "superBot">): boolean {
  if (bot.superBot) return false;
  if ((RESEARCH_DEPARTMENTS as readonly string[]).includes(bot.key)) return true;
  return !(DEPARTMENTS as readonly string[]).includes(bot.key) && RESEARCH_NAME.test(bot.name);
}

const DEPARTMENT_DEFAULTS: Record<Department, DepartmentDefault> = {
  operations: {
    name: "Operations Manager",
    role: "Daily ops summary, staffing gaps, issues and escalations",
    duties: [
      "Write a short daily operations summary: what is late, what is at risk, what needs a decision.",
      "Track open issues and escalations and chase their owners.",
      "Flag staffing or capacity gaps early.",
    ],
    zone: "Operations",
  },
  compliance: {
    name: "Compliance Officer",
    role: "Contract register, policy reviews, regulatory deadlines",
    duties: [
      "Keep a register of contracts, policies, certificates and licences with their renewal dates.",
      "Remind the owner well before anything expires and list what is overdue.",
      "Prepare policy review drafts; never sign or submit anything.",
    ],
    zone: "Compliance",
  },
  finance: {
    name: "Finance Assistant",
    role: "Invoicing, payment chasing, weekly cash summary",
    duties: [
      "Draft invoices and polite payment reminders for overdue invoices.",
      "Write a weekly cash summary: money in, money owed, money due out.",
      "Never send money, change bank details or approve a payment.",
    ],
    zone: "Finance",
  },
  bids: {
    name: "Bid Writer",
    role: "Finds tenders that fit, drafts bids and method statements for your review",
    duties: [
      "Search for tenders and opportunities that fit the company's services and size.",
      "Draft bids, method statements and answers to questions for review.",
      "Record the pricing assumptions behind each bid.",
    ],
    zone: "Sales",
  },
  hr: {
    name: "HR Coordinator",
    role: "Onboarding, holidays, documents",
    duties: [
      "Run onboarding checklists for new starters.",
      "Track holiday requests and the documents staff still owe.",
      "Draft job adverts and screen CVs against the role when asked.",
    ],
    zone: "HR",
  },
  sales: {
    name: "Sales Development",
    role: "Prospecting, follow-ups, CRM",
    duties: [
      "Find prospects that match the company's best customers.",
      "Draft follow-ups and keep the CRM up to date.",
      "Report the pipeline weekly.",
    ],
    zone: "Sales",
  },
  customer_service: {
    name: "Customer Service",
    role: "Client emails, complaints, updates",
    duties: [
      "Triage client emails and draft replies in the company's tone.",
      "Track complaints until they are resolved.",
      "Keep a list of common questions and their answers.",
    ],
    zone: "Customer service",
  },
  marketing: {
    name: "Marketing Assistant",
    role: "Website, posts, case studies",
    duties: [
      "Keep a content calendar and draft posts and case studies.",
      "Suggest website updates from recent work.",
      "Report what was published each month.",
    ],
    zone: "Marketing",
  },
};

function chief(): TeamBot {
  return {
    key: CHIEF_KEY,
    name: CHIEF_NAME,
    role: CHIEF_ROLE,
    tools: [],
    zone: null,
    walled: false,
    superBot: true,
    suggested: false,
  };
}

function departmentBot(department: Department): TeamBot {
  const role = DEPARTMENT_DEFAULTS[department];
  return {
    key: department,
    name: role.name,
    role: role.role,
    tools: [],
    zone: role.zone,
    walled: false,
    superBot: false,
    suggested: false,
  };
}

/** Chief as a team member, for its starting instructions. */
export function chiefTeamBot(): TeamBot {
  return chief();
}

const AUTONOMY_LINES: Record<Autonomy, string> = {
  draft: "Prepare drafts and plans for the owner to approve; do not act on your own.",
  ask: "Do the work yourself, but ask before sending anything to anyone outside the company.",
  act: "Act on your own within your role and report what you did.",
};

const ALWAYS_ASK =
  "Always ask before sending anything to clients, making or approving a payment, or deleting anything.";

/** The approvals block every team bot's instructions end with. */
export function approvalsSection(autonomy: Autonomy | undefined): string {
  return `## Approvals\n${AUTONOMY_LINES[autonomy ?? "ask"]} ${ALWAYS_ASK}`;
}

/** Plain one-line facts about the company for a bot's instructions. */
function companyLines(answers: CompanyAnswers, industryLabel: string | null): string[] {
  const name = answers.name?.trim();
  const facts = [
    name ? `You work for ${name}` : "You work for this company",
    industryLabel ? `, in ${industryLabel}` : "",
    answers.country ? ` (${answers.country})` : "",
    answers.size ? `, about ${answers.size} people` : "",
    ".",
  ].join("");
  return [
    facts,
    answers.website ? `Website: ${answers.website}.` : "",
    answers.description ? `What the company does: ${answers.description}` : "",
  ].filter(Boolean);
}

export interface InstructionContext {
  answers: CompanyAnswers;
  /** The industry in words: its label, or the owner's own text for "other". */
  industryLabel: string | null;
  /** The 90-day results in words. */
  goalLabels: string[];
  team: TeamBot[];
}

/** A bot's starting instructions, seeded from the company profile and goals. */
export function teamBotInstructions(bot: TeamBot, context: InstructionContext): string {
  const { answers } = context;
  const department = (DEPARTMENTS as readonly string[]).includes(bot.key)
    ? (bot.key as Department)
    : null;
  const duties = department
    ? DEPARTMENT_DEFAULTS[department].duties
    : bot.superBot
      ? [
          "Check the other bots' work, nudge stalled jobs and keep each bot in its role.",
          "Send the owner one daily summary with the decisions they need to make.",
        ]
      : [];
  const others = context.team.filter((other) => other.key !== bot.key).map((other) => other.name);
  const goals = [
    ...context.goalLabels,
    answers.goalsOther?.trim(),
    answers.goalsText?.trim(),
  ].filter(Boolean);
  const sections = [
    `You are ${bot.name}${bot.role ? `: ${bot.role}` : ""}.`,
    companyLines(answers, context.industryLabel).join(" "),
    duties.length ? `## How you work\n${duties.map((duty) => `- ${duty}`).join("\n")}` : "",
    goals.length
      ? `## Results the owner wants in the next 90 days\n${goals.map((goal) => `- ${goal}`).join("\n")}`
      : "",
    bot.tools.length
      ? `## Apps\n${bot.tools.join(", ")}. You only get the ones the owner connects.`
      : "",
    others.length ? `## Your team\n${others.join(", ")}.` : "",
    botWorkRulesSection({ research: isResearchRole(bot) }),
    approvalsSection(answers.autonomy),
    bot.superBot
      ? ""
      : "Learn the business first: in your first conversation, ask a few questions at a time about how the company works in your area, save the answers to memory, and ask rather than guess.",
  ];
  return sections.filter(Boolean).join("\n\n");
}

/**
 * A ready-made bot from the team builder's department defaults, offered in "Start from". Like
 * wizard bots, it keeps every built-in tool on, so research roles have web search.
 */
export interface BuiltinBotTemplate {
  /** `builtin:<department>`. */
  id: string;
  name: string;
  title: string;
  description: string;
  instructions: string;
}

export const BUILTIN_TEMPLATE_PREFIX = "builtin:";

/** "Start from" for the Website teammate; creating from it also sets up its paused upkeep. */
export const WEBSITE_TEMPLATE_ID = `${BUILTIN_TEMPLATE_PREFIX}website`;

function websiteTemplate(): BuiltinBotTemplate {
  const recipe = roleRecipe("website");
  return {
    id: WEBSITE_TEMPLATE_ID,
    name: "Website",
    title: recipe.title,
    description: "Keeps your site's SEO and AI readability up to date, one change a day",
    instructions: [
      `You are the ${recipe.title}.`,
      `## How you work\n${recipe.duties.map((duty) => `- ${duty}`).join("\n")}`,
      recipeLeadSection(recipe),
      botWorkRulesSection({ research: false }),
      approvalsSection(undefined),
    ].join("\n\n"),
  };
}

/** The department defaults as templates, with the same instructions the wizard seeds. */
export function builtinBotTemplates(): BuiltinBotTemplate[] {
  return [...departmentTemplates(), websiteTemplate()];
}

function departmentTemplates(): BuiltinBotTemplate[] {
  return DEPARTMENTS.map((department) => {
    const bot = departmentBot(department);
    const context = { answers: {}, industryLabel: null, goalLabels: [], team: [bot] };
    return {
      id: `${BUILTIN_TEMPLATE_PREFIX}${department}`,
      name: bot.name,
      title: bot.name,
      description: bot.role,
      instructions: teamBotInstructions(bot, context),
    };
  });
}

/**
 * The approval settings an autonomy level means. Never ask would skip every rule, so no level
 * uses it: client emails, payments and deletions always keep a rule that asks.
 */
export function autonomyApprovalSettings(autonomy: Autonomy | undefined): {
  mode: ApprovalMode;
  rules: Array<Pick<ActionApprovalRule, "effect" | "matchKind" | "matchValue">>;
} {
  const always = ["email", "purchase", "delete"].map((value) => ({
    effect: "require_approval" as const,
    matchKind: "category" as const,
    matchValue: value,
  }));
  const level = autonomy ?? "ask";
  // Asking before sending covers chat apps as well as email.
  const sending =
    level === "act"
      ? []
      : ["slack", "whatsapp", "microsoftteams"].map((value) => ({
          effect: "require_approval" as const,
          matchKind: "connector" as const,
          matchValue: value,
        }));
  return { mode: level === "draft" ? "ask" : "auto", rules: [...always, ...sending] };
}

/** English names for the bots' instructions; the apps show their own translated labels. */
export const INDUSTRY_NAMES: Record<Industry, string> = {
  security_fm: "security and facilities management",
  cleaning: "cleaning",
  construction: "construction",
  property: "property",
  professional_services: "professional services",
  accounting: "accounting",
  legal: "legal services",
  consulting: "consulting",
  marketing_agency: "marketing and creative",
  software: "software and SaaS",
  it_services: "IT services",
  telecoms: "telecoms",
  retail: "retail",
  ecommerce: "e-commerce",
  wholesale: "wholesale and distribution",
  manufacturing: "manufacturing",
  engineering: "engineering",
  logistics: "logistics",
  transport: "transport",
  hospitality: "hospitality",
  food_beverage: "food and drink",
  events: "events",
  travel: "travel and tourism",
  healthcare: "healthcare",
  dental: "dental",
  care: "social care",
  pharma: "pharmaceuticals and life sciences",
  fitness: "fitness and leisure",
  beauty: "beauty and wellbeing",
  education: "education",
  childcare: "childcare",
  charity: "charity and non-profit",
  public_sector: "the public sector",
  financial_services: "financial services",
  insurance: "insurance",
  recruitment: "recruitment",
  energy: "energy and utilities",
  agriculture: "agriculture",
  automotive: "automotive",
  media: "media and publishing",
  architecture: "architecture and design",
  trades: "trades and home services",
  other: "",
};

export const GOAL_NAMES: Record<Goal, string> = {
  no_uncovered_shifts: "No uncovered shifts",
  paid_faster: "Get paid faster",
  win_tenders: "Win more tenders",
  no_missed_renewals: "Never miss a licence or renewal",
  fewer_admin_hours: "Fewer admin hours",
  faster_replies: "Faster replies to customers",
  more_leads: "More leads",
  hire_faster: "Hire faster",
};

/** The industry in words for instructions: its name, or the owner's text for "other". */
export function industryName(answers: CompanyAnswers): string | null {
  if (!answers.industry) return null;
  if (answers.industry === "other") return answers.industryOther?.trim() || null;
  return INDUSTRY_NAMES[answers.industry];
}
